import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import CardRow from '../../components/card/CardRow';
import LoginHeader from '../../components/LoginHeader/LoginHeader';
import loginKeycloakStyle from './loginKeycloak.module.scss';
import configs from '../../configs';
import { useKeycloak } from '../../contexts/KeycloakAuth';
import { ClbButton, ClbCard, ClbLoading, useToast } from '@celebration/react';
import { useUser } from '../../contexts/User';
import { getApiErrorMessage } from '../../utils/apiError';
import LoginService from '../services/login/sso.service';
import UserService from '../services/users/user.service';
import AppService from '../../app.service';
import {
  exchangeAuthCodeForToken,
  initKeycloak,
  loginSilentlyWithKeycloak,
  getKeycloakInstance,
  checkKeycloakHealth,
  resetKeycloakInstance,
  logoutKeycloak,
  retryWithFreshCode,
} from '../../utils/keycloakAuth';
import {
  KEYCLOAK_AUTO_LOGIN_HINT_KEY,
  KeycloakRealm,
  resolveSelectedRealm,
} from '../../utils/keycloakConfig';
import { msalConfig } from '../../utils/authConfig';
import {
  buildMsLogoutUrl,
  isMsalAuthResponseInUrl,
} from '../../utils/msalLogout';
import { changeLanguage, getAllStorage, setCookie } from '../../utils/utils';
import { DataDogRum } from '../../utils/dataDogRum';
import moment from 'moment';
const Login = React.lazy(() => import('../Login/Login'));
const LoginSSO = React.lazy(() => import('../LoginSSO/LoginSSO'));

const LoginKeycloak = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { instance: msalInstance } = useMsal();
  const [loading, setLoading] = useState(false);
  // Fallback state: 'checking' | 'available' | 'fallback'
  // 'checking' (validateVersion in flight — the page stays behind a loading
  // screen until the backend's bol_kc_logon flag is known) | 'available'
  // (show KC login) | 'fallback' (KC down or disabled, show MSAL)
  const [kcStatus, setKcStatus] = useState('checking');
  const [kcHealthStatus, setKcHealthStatus] = useState('checking');
  const [appVersion, setAppVersion] = useState(
    () => localStorage.getItem('version') || configs.val_version || '',
  );
  // When true, the component is mid-redirect to Microsoft logout (chained after KC logout)
  const [msLogoutPending, setMsLogoutPending] = useState(
    () => sessionStorage.getItem('kc_logout_pending') === 'true',
  );
  const { loginAsInternalUser, loginAsExternalUser } = useKeycloak();
  const { handleUser, handleLogged } = useUser();
  const loginService = new LoginService();
  const userService = new UserService();
  const appService = new AppService();
  const hasProcessedCallback = useRef(false);
  const hasSilentLoginBeenCalled = useRef(false);
  const isNotPc = window.navigator.userAgent?.match(
    /Mobile|Android|iPhone|iPad|iPod/i,
  );
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  // Touch devices (iPad/tablet/phone). Modern iPads (iPadOS 13+) report a
  // desktop "Macintosh" UA in Safari's default desktop mode, so
  // maxTouchPoints is required to detect them — same detection logic as
  // msalLogout.js's isMobileDevice.
  const isTouchDevice =
    /Mobile|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent) ||
    (window.navigator.maxTouchPoints > 1 &&
      /Macintosh/i.test(window.navigator.userAgent));
  const isSafariLike =
    /Safari/i.test(window.navigator.userAgent) &&
    !/(Chrome|CriOS|FxiOS|Edg|Android)/i.test(window.navigator.userAgent);
  const isMobileSafari = isTouchDevice && isSafariLike;
  // Chained Microsoft logout — after Keycloak logout redirect brings the user
  // back to the app, also terminate the Microsoft IDP session so the next login
  // doesn't auto-SSO through the still-active Microsoft session.
  // Only triggered when the user logged in via the SSO button (first button),
  // which sets the `need_ms_logout` flag. Third-party logins skip MS logout.
  // The re-entry guard in Logout.jsx (isLoggingOut flag) prevents a redirect
  // loop if MS logout sends the browser back to /logout.
  useEffect(() => {
    if (!msLogoutPending) return;

    const performMsLogout = async () => {
      sessionStorage.removeItem('kc_logout_pending');

      // Check whether the user logged in via the SSO button.
      // The `need_ms_logout` flag is set in handleSSOLogin before redirecting
      // to Keycloak, and persists through the entire session via sessionStorage.
      const needMsLogout =
        sessionStorage.getItem('need_ms_logout') === 'true' ||
        sessionStorage.getItem('kc_login_source') === 'silent' ||
        sessionStorage.getItem('kc_login_source') === 'sso';
      sessionStorage.removeItem('need_ms_logout');
      sessionStorage.removeItem('kc_login_source');

      if (!needMsLogout) {
        // Third-party login or direct KC login — no Microsoft session to
        // terminate. Clear the re-entry guard so the user can log in again.
        sessionStorage.removeItem('isLoggingOut');
        sessionStorage.removeItem('ms_logout_hint');
        sessionStorage.removeItem('ms_id_token_hint');
        setMsLogoutPending(false);
        return;
      }

      // The Microsoft logout was already opened in a separate tab when the
      // logout button was clicked (user-gesture context — popup-blocker
      // safe). That tab terminates the Microsoft SSO session. This tab
      // (back from the Keycloak logout redirect) just returns to the login
      // page: navigating THIS tab to Microsoft without an id_token_hint is
      // what strands the user on the "You signed out" page on iPad.
      if (sessionStorage.getItem('ms_logout_opened') === 'true') {
        sessionStorage.removeItem('ms_logout_opened');
        sessionStorage.removeItem('isLoggingOut');
        sessionStorage.removeItem('ms_logout_hint');
        sessionStorage.removeItem('ms_id_token_hint');
        setMsLogoutPending(false);
        return;
      }

      const logoutHint = sessionStorage.getItem('ms_logout_hint');
      const idTokenHint = sessionStorage.getItem('ms_id_token_hint');
      sessionStorage.removeItem('ms_logout_hint');
      sessionStorage.removeItem('ms_id_token_hint');
      // Build the manual end-session URL lazily — only used by fallback paths
      // (mobileSafari, touch devices, no MSAL account, or logoutRedirect
      // failure). The preferred path (logoutRedirect with a cached account)
      // does not need it.
      const getMsLogoutUrl = () =>
        buildMsLogoutUrl({
          authority: msalConfig.auth.authority,
          clientId: msalConfig.auth.clientId,
          origin: window.location.origin,
          logoutHint: logoutHint || undefined,
          idTokenHint: idTokenHint || undefined,
        });

      // Safari WebKit may not expose or reliably use the MSAL account created
      // during the Keycloak SAML flow, including on desktop Safari. Use the
      // OIDC end-session URL directly so every Safari logout reaches
      // Microsoft's logout endpoint.
      if (isSafariLike) {
        window.location.href = getMsLogoutUrl();
        return;
      }

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        if (isTouchDevice) {
          // Touch device fallback (the new-tab logout did not happen, e.g.
          // the popup was blocked): build the manual end-session URL. The
          // id_token_hint is REQUIRED for Microsoft to honor
          // post_logout_redirect_uri; without it the user is stranded on
          // the "You signed out" page.
          window.location.href = getMsLogoutUrl();
        } else {
          try {
            await msalInstance.logoutRedirect({
              account: accounts[0],
              postLogoutRedirectUri: window.location.origin,
              logoutHint: logoutHint || undefined,
            });
          } catch {
            // logoutRedirect failed — fall back to the manual end-session
            // URL (client_id + logout_hint + post_logout_redirect_uri).
            window.location.href = getMsLogoutUrl();
          }
        }
      } else {
        // No MSAL account (the login-time silent auth was unavailable —
        // common on iPad/tablet browsers). Fallback: redirect to
        // Microsoft's end-session endpoint with the manual URL.
        window.location.href = getMsLogoutUrl();
      }
    };

    performMsLogout();
  }, [msLogoutPending]);

  useEffect(() => {
    if (!msLogoutPending) {
      sessionStorage.removeItem('isLoggingOut');
    }
  }, [msLogoutPending]);

  useEffect(() => {
    if (msLogoutPending) {
      return;
    }

    if (hasAuthCodeInUrl()) {
      // Auth-code callback flow — handleAuthCallback manages its own loading
      // state and navigates away, so the pre-login checks are skipped.
      setKcStatus('available');
      return;
    }

    let cancelled = false;
    // Step 1 — validateVersion must finish BEFORE the login page is shown.
    // The version endpoint exposes bol_kc_logon — when the backend reports
    // it as false, Keycloak SSO login is disabled and we fall back to the
    // direct MSAL login (LoginSSO). A failed request is treated as "KC
    // enabled" so a backend hiccup cannot blank the login page.
    appService
      .validateVersion()
      .catch(() => null)
      .then(versionData => {
        if (cancelled) return;
        const version = versionData?.results?.[0]?.val_version;
        if (version) setAppVersion(version);
        const bolKcLogon = versionData?.results?.[0]?.bol_kc_logon;
        if (bolKcLogon === false) {
          setKcHealthStatus('unavailable');
          setKcStatus('fallback');
          return;
        }

        // Step 2 — the login page is now visible and Keycloak login is
        // enabled, so probe KC reachability in the background; fall back
        // to the MSAL login when Keycloak itself is unreachable.
        setKcStatus('available');
        checkKeycloakHealth()
          .catch(() => false)
          .then(isHealthy => {
            if (cancelled) return;
            if (!isHealthy) {
              setKcHealthStatus('unavailable');
              setKcStatus('fallback');
              return;
            }
            setKcHealthStatus('available');
          });
      });
    return () => {
      cancelled = true;
    };
  }, [msLogoutPending]);

  // An MSAL response in the URL must NOT be treated as a Keycloak auth-code
  // callback — exchangeAuthCodeForToken would fail on the MSAL auth code.
  // isMsalAuthResponseInUrl (utils/msalLogout) detects MSAL responses via
  // the 'state' + 'client_info' parameters that Keycloak doesn't send.
  const hasAuthCodeInUrl = () => {
    if (isMsalAuthResponseInUrl()) return false;
    return (
      window.location.search.includes('code=') ||
      window.location.hash.includes('code=')
    );
  };

  const hasKeycloakErrorInUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    );
    return params.has('error') || hashParams.has('error');
  };

  const cleanAuthCodeFromUrl = () => {
    if (hasAuthCodeInUrl()) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Complete the application login after Keycloak has authenticated. This is
  // shared by the callback flow and the silent check-sso flow so a returning
  // user follows the same path as a freshly authenticated user.
  const handleAuthenticatedKeycloak = async (
    keycloak,
    { silent = false } = {},
  ) => {
    const tokenParsed = keycloak?.tokenParsed;
    if (!keycloak?.token || !tokenParsed) {
      throw new Error('Keycloak token exchange failed');
    }

    if (keycloak.idToken) {
      sessionStorage.setItem('kc_id_token', keycloak.idToken);
    }

    const res = await loginService.loginKeycloakAuth({
      str_email: tokenParsed.email || tokenParsed.preferred_username,
      str_token: keycloak.token,
      objectId: tokenParsed.preferred_username,
      response: JSON.stringify(tokenParsed),
    });

    if (!(res.type === 'success' && res.results)) {
      throw Object.assign(
        new Error(getApiErrorMessage(res, 'Backend authentication failed')),
        {
          backendAuthFailed: true,
        },
      );
    }

    sessionStorage.removeItem('kc_auth_retry_count');
    const results = Array.isArray(res.results) ? res.results[0] : res.results;
    setCookie('str_auth', results.str_token);
    sessionStorage.setItem('java-token', results.str_token);
    sessionStorage.setItem('auth_method_used', 'keycloak');
    // Remember that a real KC login succeeded. Safari cannot reliably use
    // the hidden check-sso iframe, so it uses a guarded top-level prompt=none
    // redirect on subsequent visits instead.
    localStorage.setItem(KEYCLOAK_AUTO_LOGIN_HINT_KEY, 'true');

    // A returning internal-realm session may have been created through the
    // Microsoft SSO broker. Preserve that information for the later logout
    // flow so Microsoft is signed out together with Keycloak.
    if (
      silent &&
      resolveSelectedRealm() === KeycloakRealm.Internal &&
      !sessionStorage.getItem('need_ms_logout')
    ) {
      sessionStorage.setItem('need_ms_logout', 'true');
    }
    sessionStorage.setItem(
      'kc_login_source',
      silent
        ? 'silent'
        : sessionStorage.getItem('need_ms_logout') === 'true'
        ? 'sso'
        : 'third_party',
    );

    if (keycloak.idToken) {
      sessionStorage.setItem('kc_id_token', keycloak.idToken);
    }

    assertLogin(
      { ...results, objectId: tokenParsed.preferred_username },
      tokenParsed.preferred_username,
    );

    // A silent check is already an authenticated SSO session. Do not start a
    // second Microsoft redirect during this path; just enter the app.
    if (silent || sessionStorage.getItem('need_ms_logout') !== 'true') {
      cleanAuthCodeFromUrl();
      navigate('/', { replace: true });
      return;
    }

    const userEmail = tokenParsed.email || tokenParsed.preferred_username;
    cleanAuthCodeFromUrl();
    const msalLoginRequest = {
      scopes: ['openid', 'profile'],
      loginHint: userEmail,
    };
    if (!isTouchDevice) {
      msalLoginRequest.prompt = 'none';
    }
    msalInstance.loginRedirect(msalLoginRequest);
  };

  const assertLogin = (ssoResult, keycloakSub) => {
    if (!ssoResult) {
      setLoading(false);
      return;
    }

    sessionStorage.removeItem('userLogout');

    const showMenuTemp = !!(
      ssoResult.arr_privileges &&
      ssoResult.arr_privileges.length > 0 &&
      !(
        ssoResult.arr_privileges.length === 1 &&
        ssoResult.arr_privileges[0] === 'TASTER'
      )
    );

    const login_date = moment(new Date()).format('MM/DD/YYYY HH:mm:ss');
    const userTemp = {
      ...ssoResult,
      currentTab: 'menu_tasting_session',
      currentMenu: 'menu_tasting_session',
      showMenu: showMenuTemp,
      bol_lock_privileges: !!ssoResult?.bol_lock_privileges,
      unique_id: ssoResult.objectId ?? keycloakSub ?? '',
      login_date,
    };

    const role =
      ssoResult.arr_privileges?.length === 1 &&
      ssoResult.arr_privileges[0] === 'TASTER'
        ? 'taster'
        : ssoResult.arr_privileges?.length === 1 &&
          ssoResult.arr_privileges[0] === 'PROCESS_CHECK'
        ? 'process_check'
        : 'admin';
    const metadata = {
      zone: `${ssoResult.str_name_zone ?? 'N/A'}`,
      country: `${ssoResult.str_name_country ?? 'N/A'}`,
      site: `${ssoResult.str_name_site ?? 'N/A'}`,
      userRole: `${role}`,
    };
    const metadataJson = JSON.stringify(metadata);
    const linkUrl =
      configs.env === 'prod'
        ? 'https://nps-new.ambevdevs.com.br'
        : 'https://nps-new-stg.ambevdevs.com.br';
    const linkId =
      configs.env === 'prod'
        ? 'ab98c90c-8ebf-4cbd-0942-08dca554381b'
        : 'f7c0ba84-e8a7-42e2-0358-08dca5543c34';
    const scriptDom = document.createElement('script');
    scriptDom.src = linkUrl;
    scriptDom.setAttribute('host', linkUrl);
    scriptDom.setAttribute('system', linkId);
    scriptDom.setAttribute('auth', 'idtoken');
    scriptDom.setAttribute('property', 'secret');
    scriptDom.setAttribute('storage', isNotPc ? 'session' : 'local');
    scriptDom.setAttribute('metadata', metadataJson);
    scriptDom.setAttribute('productId', '510');
    document.body.appendChild(scriptDom);

    handleUser(userTemp);
    handleLogged(true);
    sessionStorage.removeItem('userLogout');
    window.dispatchEvent(new Event('userLogoutChanged'));
    changeLanguage(i18n, ssoResult.str_language);
    DataDogRum(userTemp);

    // NPS log — consistent with LoginSSO
    const data = {
      uuid_user: ssoResult?.uuid_user,
      str_name: ssoResult?.str_name,
      uuid_zone: ssoResult?.uuid_zone,
      uuid_country: ssoResult?.uuid_country,
      uuid_site: ssoResult?.uuid_site,
      str_email: ssoResult?.str_email,
    };
    userService.createNpsLog(data).then(res => {
      if (res) {
        const timer = setTimeout(() => {
          const intervalTimer = setInterval(() => {
            const initial_error_message = isNotPc
              ? getAllStorage('session')
              : getAllStorage('local');
            if (initial_error_message) {
              clearInterval(intervalTimer);
              userService.submitNpsDurations({
                ...data,
                txt_local_story: JSON.stringify(getAllStorage('local')),
                txt_session_story: JSON.stringify(getAllStorage('session')),
                str_device: window.navigator.userAgent,
              });
            }
          }, 100);
          clearTimeout(timer);
        }, 5000);
      }
    });
  };

  /**
   * Handle Keycloak callback — imitates LoginAuth's handleLogin
   *
   * Flow: exchangeAuthCodeForToken (direct POST to token endpoint, bypasses
   *       keycloak.init() iframe SSO check) → loginKeycloakAuth (backend auth)
   *       → assertLogin (set user) → redirect
   *
   * Uses exchangeAuthCodeForToken() instead of initKeycloak() to avoid the
   * "3rd party check iframe" timeout that occurs when keycloak.init() tries
   * to use a hidden iframe for SSO session checking in cross-origin environments.
   * This is a direct HTTP POST to the Keycloak token endpoint — a single
   * network request instead of the full init flow.
   */
  const handleAuthCallback = async () => {
    setLoading(true);
    try {
      // 1. Exchange auth code for tokens — bypasses keycloak.init() iframe
      //    check, directly POSTs to the Keycloak token endpoint.
      const authenticated = await exchangeAuthCodeForToken();
      if (!authenticated) {
        throw new Error('Keycloak authentication failed');
      }
      const keycloak = getKeycloakInstance();
      const tokenParsed = keycloak.tokenParsed;

      if (!keycloak.token || !tokenParsed) {
        throw new Error('Keycloak token exchange failed');
      }

      // Store id_token early — needed for Keycloak logout (id_token_hint) if
      // backend auth fails and we need to disconnect the KC SSO session.
      if (keycloak.idToken) {
        sessionStorage.setItem('kc_id_token', keycloak.idToken);
      }

      const res = await loginService.loginKeycloakAuth({
        str_email: tokenParsed.email || tokenParsed.preferred_username,
        str_token: keycloak.token,
        objectId: tokenParsed.preferred_username,
        response: JSON.stringify(tokenParsed),
      });

      if (res.type === 'success' && res.results) {
        const isSafariSilentCallback =
          sessionStorage.getItem('kc_login_source') === 'silent';
        // Reset the auth retry counter — this login attempt succeeded
        sessionStorage.removeItem('kc_auth_retry_count');
        const results = Array.isArray(res.results)
          ? res.results[0]
          : res.results;
        setCookie('str_auth', results.str_token);
        sessionStorage.setItem('java-token', results.str_token);
        sessionStorage.setItem('auth_method_used', 'keycloak');
        // Safari's top-level prompt=none flow returns through this same
        // authorization-code callback. It bypasses handleSSOLogin, so seed
        // the Microsoft logout chain here for the internal SSO realm.
        if (
          isSafariSilentCallback &&
          resolveSelectedRealm() === KeycloakRealm.Internal
        ) {
          sessionStorage.setItem('need_ms_logout', 'true');
        }
        // Seed the Safari silent-login marker after an explicit callback too.
        // Without this, Safari would have no way to distinguish a first visit
        // from a returning KC session and would either skip auto-login or loop.
        localStorage.setItem(KEYCLOAK_AUTO_LOGIN_HINT_KEY, 'true');
        sessionStorage.setItem(
          'kc_login_source',
          sessionStorage.getItem('need_ms_logout') === 'true'
            ? 'sso'
            : 'third_party',
        );

        // Store id_token for Keycloak logout (id_token_hint parameter).
        // Keycloak 26.x requires id_token_hint to auto-redirect back after
        // logout instead of showing a confirmation page.
        // Stored in sessionStorage to survive page refresh / HMR.
        if (keycloak.idToken) {
          sessionStorage.setItem('kc_id_token', keycloak.idToken);
        }

        assertLogin(
          {
            ...results,
            objectId: tokenParsed.preferred_username,
          },
          tokenParsed.preferred_username,
        );

        // Establish an MSAL account so the chained MS logout can send
        // id_token_hint. This is a full page redirect — the browser
        // navigates to Microsoft's /authorize endpoint, which uses the MS
        // session cookie (set during the SAML SSO via Keycloak) as
        // first-party. This bypasses the third-party cookie restrictions
        // that block ssoSilent (hidden iframe) and the popup blockers that
        // block loginPopup.
        //
        // If successful, MSAL establishes an account with an id_token. At
        // logout time, msalInstance.logoutRedirect() automatically includes
        // this id_token as id_token_hint, which per OIDC spec forces
        // Microsoft to redirect back to postLogoutRedirectUri.
        //
        // Device split: iPad/tablet browsers (all WebKit on iPadOS) reject
        // silent auth (prompt='none') — no MSAL account gets established,
        // so at logout there is no id_token_hint and Microsoft drops the
        // post-logout redirect (the "stuck on You signed out" bug). On
        // touch devices use an interactive loginRedirect instead: the
        // still-active MS session from the SAML SSO login + loginHint
        // normally completes with zero user interaction — the same login
        // flow the pre-KC LoginSSO used, which is proven to work on iPad;
        // worst case the user taps their account once. Desktop keeps
        // prompt='none' — silent auth works there and stays invisible.
        //
        // Only do this for SSO logins (need_ms_logout='true'). Third-party
        // logins skip MS authentication entirely.
        if (sessionStorage.getItem('need_ms_logout') === 'true') {
          const userEmail = tokenParsed.email || tokenParsed.preferred_username;
          // Clean the auth code from URL before navigating away —
          // when the browser returns from MSAL, the URL should be clean.
          cleanAuthCodeFromUrl();
          const msalLoginRequest = {
            scopes: ['openid', 'profile'],
            loginHint: userEmail,
          };
          if (!isTouchDevice) {
            // Desktop only — iPadOS WebKit refuses prompt='none'
            msalLoginRequest.prompt = 'none';
          }
          // loginRedirect navigates the browser away — the Promise never
          // resolves. assertLogin has already persisted the user session
          // to sessionStorage, so when the browser returns, UserProvider
          // reads it and Routes renders the main app directly.
          msalInstance.loginRedirect(msalLoginRequest);
          return; // Browser will navigate — don't call navigate('/')
        }

        cleanAuthCodeFromUrl();
        navigate('/', { replace: true });
      } else {
        // Backend auth failed (401, invalid token, user not found, etc.)
        // Show toast, then disconnect KC SSO session so the user can re-login.
        // need_ms_logout is preserved from handleSSOLogin/handleThirdPartyLogin:
        //   - SSO login → need_ms_logout='true'  → KC logout chains to MS logout
        //   - Third Party → need_ms_logout='false' → KC logout only, no MS logout
        console.error('Backend auth failed:', getApiErrorMessage(res));
        cleanAuthCodeFromUrl();
        toast.openToast({
          type: 'warning',
          description: getApiErrorMessage(res, 'Backend authentication failed'),
          timeToClose: 6000,
        });
        setLoading(false);
        setTimeout(() => {
          logoutKeycloak();
        }, 2000);
      }
    } catch (err) {
      // Network error, init failure, or any other exception during auth callback
      console.error('Keycloak auth callback error:', err);
      cleanAuthCodeFromUrl();

      // Retryable: authorization code expired or already consumed (400
      // "Code not valid"). On slow networks (CN→BR) the time between KC's
      // redirect and this useEffect can exceed the code's 60s lifetime.
      // Instead of dead-ending, silently redirect to KC for a fresh code —
      // the user's KC/SAML session is still active, so no re-prompt occurs.
      // Max 1 retry (tracked in sessionStorage) to prevent redirect loops.
      if (err?.isRetryable) {
        const retryCount = parseInt(
          sessionStorage.getItem('kc_auth_retry_count') || '0',
          10,
        );
        if (retryCount < 1) {
          sessionStorage.setItem('kc_auth_retry_count', String(retryCount + 1));
          retryWithFreshCode(); // navigates away — no further code runs
          return;
        }
        // Retry budget exhausted → reset counter, fall through to error UI
        sessionStorage.removeItem('kc_auth_retry_count');
      }

      toast.openToast({
        type: 'warning',
        description:
          getApiErrorMessage(
            err,
            'Unable to connect to the authentication server',
          ),
        timeToClose: 6000,
      });
      setLoading(false);
      setTimeout(() => {
        logoutKeycloak();
      }, 2000);
    }
  };

  const handleSilentLogin = async () => {
    if (hasSilentLoginBeenCalled.current) return;
    hasSilentLoginBeenCalled.current = true;
    setLoading(true);

    // Browsers with an existing KC session marker use a guarded top-level
    // prompt=none redirect. This is required by Safari and also fixes
    // Chromium browsers where third-party-cookie policy blocks check-sso's
    // hidden iframe. Without the marker, keep the first visit on the login
    // page instead of redirecting to KC and returning with #error=login_required.
    const hasKnownKeycloakSession =
      localStorage.getItem(KEYCLOAK_AUTO_LOGIN_HINT_KEY) === 'true';
    // Mobile browsers other than Safari do not support this KC silent-login
    // flow reliably; leave the normal login button available for them.
    if (isTouchDevice && !isSafariLike) {
      setLoading(false);
      return;
    }

    // Mobile Safari needs a top-level redirect because its hidden check-sso
    // iframe cannot access the KC session cookie. Do not use a persistent
    // per-tab redirect-attempt flag here: Safari can carry sessionStorage
    // across a duplicated/restored tab and incorrectly block later tabs.
    if (isMobileSafari) {
      if (!hasKnownKeycloakSession) {
        sessionStorage.removeItem('kc_safari_redirect_attempted');
        setLoading(false);
        return;
      }
      sessionStorage.removeItem('kc_safari_redirect_attempted');
      sessionStorage.setItem('kc_login_source', 'silent');
      await loginSilentlyWithKeycloak();
      return;
    }

    // Chrome/Edge desktop may block the hidden cross-site check-sso iframe
    // after the first successful tab. Once a KC login has been confirmed,
    // use the first-party top-level prompt=none flow for every new tab. This
    // is not a one-attempt guard: each desktop tab can perform the check.
    if (!isTouchDevice && hasKnownKeycloakSession) {
      sessionStorage.setItem('kc_login_source', 'silent');
      await loginSilentlyWithKeycloak();
      return;
    }

    try {
      // Legacy sessions without a local marker still use check-sso. New
      // successful logins seed the marker and use the guarded top-level flow
      // on subsequent visits, including Chromium browsers with blocked
      // third-party cookies.
      const authenticated = await initKeycloak({
        onLoad: 'check-sso',
        redirectUri: window.location.origin,
        checkLoginIframe: false,
      });

      if (authenticated) {
        await handleAuthenticatedKeycloak(getKeycloakInstance(), {
          silent: true,
        });
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.warn('Keycloak silent login unavailable:', err?.message || err);
      sessionStorage.removeItem('kc_silent_redirect_attempted');
      resetKeycloakInstance();

      // Chrome/Edge can time out while checking the KC session in a hidden
      // iframe when third-party cookies are restricted. If this browser has
      // already completed a real KC login, retry once through the top-level
      // prompt=none flow, which runs in a first-party cookie context.
      if (!isTouchDevice && hasKnownKeycloakSession) {
        sessionStorage.setItem('kc_login_source', 'silent');
        await loginSilentlyWithKeycloak();
        return;
      }

      hasSilentLoginBeenCalled.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasKeycloakErrorInUrl()) {
      sessionStorage.removeItem('kc_silent_redirect_attempted');
      sessionStorage.removeItem('kc_safari_redirect_attempted');
      sessionStorage.removeItem('kc_auth_retry_count');
      localStorage.removeItem(KEYCLOAK_AUTO_LOGIN_HINT_KEY);
      resetKeycloakInstance();
      hasSilentLoginBeenCalled.current = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      setKcStatus('available');
      setKcHealthStatus('available');
      return;
    }

    /* istanbul ignore if -- guard for React Strict Mode double-invoke; not triggered in test environment */
    if (hasProcessedCallback.current) return;
    if (!hasAuthCodeInUrl()) return;

    // Additional guard: check sessionStorage for the specific auth code.
    // useRef doesn't survive page refresh or component remount — if the user
    // refreshes while the token exchange is in flight, the URL might still
    // contain ?code=xxx (exchangeAuthCodeForToken cleans it, but there's a
    // race window). This sessionStorage flag ensures a given code is only
    // processed once, even across remounts.
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    );
    const code = urlParams.get('code') || hashParams.get('code');
    const processedCode = sessionStorage.getItem('kc_code_processed');
    if (processedCode === code) {
      // This code was already submitted — clean URL and skip
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    sessionStorage.removeItem('kc_silent_redirect_attempted');
    sessionStorage.removeItem('kc_safari_redirect_attempted');
    hasProcessedCallback.current = true;
    sessionStorage.setItem('kc_code_processed', code);
    handleAuthCallback();
  }, []);

  useEffect(() => {
    if (
      msLogoutPending ||
      kcStatus !== 'available' ||
      kcHealthStatus !== 'available' ||
      hasProcessedCallback.current ||
      hasAuthCodeInUrl() ||
      hasKeycloakErrorInUrl()
    ) {
      return;
    }

    // A manual logout can return through Keycloak/Microsoft more than once.
    // Do not let check-sso race those redirects and silently log the user back
    // in before the login page has settled.
    if (sessionStorage.getItem('kc_skip_silent_login_once') === 'true') {
      sessionStorage.removeItem('kc_skip_silent_login_once');
      hasSilentLoginBeenCalled.current = true;
      return;
    }

    handleSilentLogin();
  }, [msLogoutPending, kcStatus, kcHealthStatus]);

  const handleSSOLogin = async () => {
    sessionStorage.removeItem('isLoggingOut');
    setLoading(true);
    // Mark that this login uses SSO (Microsoft IDP via SAML broker).
    // After KC logout, LoginKeycloak checks this flag to decide whether
    // to chain a Microsoft logout redirect.
    sessionStorage.setItem('need_ms_logout', 'true');
    sessionStorage.setItem('kc_login_source', 'sso');
    try {
      await loginAsInternalUser();
    } catch (err) {
      console.error('SSO login error:', err);
      sessionStorage.removeItem('need_ms_logout');
      resetKeycloakInstance();
      toast.openToast({
        type: 'warning',
        description: getApiErrorMessage(err, 'SSO login failed'),
        timeToClose: 6000,
      });
      setLoading(false);
    }
  };

  const handleThirdPartyLogin = async () => {
    sessionStorage.removeItem('isLoggingOut');
    setLoading(true);
    // Third-party login does NOT use Microsoft SSO — no MS logout needed.
    sessionStorage.setItem('need_ms_logout', 'false');
    sessionStorage.setItem('kc_login_source', 'third_party');
    try {
      await loginAsExternalUser();
    } catch (err) {
      console.error('Third-party login error:', err);
      resetKeycloakInstance();
      toast.openToast({
        type: 'warning',
        description: getApiErrorMessage(err, 'Third-party login failed'),
        timeToClose: 6000,
      });
      setLoading(false);
    }
  };

  // msLogoutPending — mid-redirect to the chained Microsoft logout.
  // kcStatus === 'checking' — validateVersion is still in flight; the login
  // page stays hidden behind a loading screen until it finishes.
  if (msLogoutPending || kcStatus === 'checking') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <ClbLoading type={'brand'} size={'lg'} />
      </div>
    );
  }

  if (kcStatus === 'fallback') {
    return (
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <ClbLoading type={'brand'} size={'lg'} />
          </div>
        }
      >
        <LoginSSO />
      </Suspense>
    );
  }
  if (showPasswordLogin && configs.env !== 'prod') {
    return <Login />;
  }
  return (
    <div className={loginKeycloakStyle.loginContainer}>
      <LoginHeader
        onLogoDoubleClick={
          configs.env !== 'prod' ? () => setShowPasswordLogin(true) : undefined
        }
      />

      <CardRow justifyContent="center" transparent>
        <ClbCard className={loginKeycloakStyle.loginBox}>
          <div>
            <h1 className={loginKeycloakStyle.loginTitle}>
              {t('access_your_account')}
            </h1>
            <p className={loginKeycloakStyle.loginDescription}>
              {t('use_your_company_credentials_to_get_started')}
            </p>
          </div>

          {loading ? (
            <CardRow justifyContent="center">
              <ClbLoading type={'brand'} size={'md'} />
            </CardRow>
          ) : (
            <>
              <ClbButton
                id="sso-login"
                label={'SSO Login'}
                type="button"
                blockedWidth={true}
                buttonStyle="primary"
                size="md"
                data-testid={'sso-login'}
                onClick={handleSSOLogin}
              />
              {/* <ClbButton
                id="third-party-login"
                label={'Third Party Login'}
                type="button"
                blockedWidth={true}
                buttonStyle="secondary"
                size="md"
                data-testid={'third-party-login'}
                onClick={handleThirdPartyLogin}
              /> */}
            </>
          )}
        </ClbCard>
      </CardRow>

      <CardRow
        itemAlign="center"
        justifyContent="center"
        className="layoutFooter"
      >
        <p>
          {configs.env !== 'prod' ? 'TEST ENVIRONMENT V' : 'V'}
          {appVersion}
        </p>
      </CardRow>
    </div>
  );
};

export default LoginKeycloak;
